import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ClassCard } from "@/components/classes/ClassCard";
import { CategoryCard } from "@/components/categories/CategoryCard";
import { useCategories } from "@/hooks/useCategories";
import { useFeaturedClasses } from "@/hooks/useClasses";
import { useHomeStats } from "@/hooks/useHomeStats";
import { useAuth } from "@/contexts/AuthContext";
import { LocationAutocomplete } from "@/components/location/LocationAutocomplete";
import { lazy, Suspense, useState, useCallback, useMemo, useEffect } from "react";
import { usePersistedLocation } from "@/hooks/usePersistedLocation";

const ReferralProgramSection = lazy(() => import("@/components/referral/ReferralProgramSection").then((m) => ({ default: m.ReferralProgramSection })));
const StudentReferralSection = lazy(() => import("@/components/referral/StudentReferralSection").then((m) => ({ default: m.StudentReferralSection })));
const SuggestionsCTAStrip = lazy(() => import("@/components/suggestions/SuggestionsCTAStrip").then((m) => ({ default: m.SuggestionsCTAStrip })));
const LeadMagnetBanner = lazy(() => import("@/components/engagement/LeadMagnetBanner").then((m) => ({ default: m.LeadMagnetBanner })));
import {
  Search,
  ArrowRight,
  Users,
  GraduationCap,
  MapPin,
  Sparkles,
  Star,
  Calendar,
  DollarSign,
  Clock,
  CheckCircle2,
  PlusCircle,
  HelpCircle } from
"lucide-react";
import { calculateDistance } from "@/hooks/useLocationSearch";

import { useNavigate } from "react-router-dom";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

// Import images

import cookingImage from "@/assets/category-cooking.webp";
import artImage from "@/assets/category-art.webp";
import faqBgImage from "@/assets/faq-strip-bg.webp";
import musicImage from "@/assets/category-music.jpg";
export default function HomePage() {
  useDocumentMeta({
    title: "Learn Around Town - Local Classes and Skill Sharing",
    description:
    "Discover local experts sharing their skills. Browse and book in-person and virtual classes in your community."
  });
  const { savedLocation, saveLocation } = usePersistedLocation();
  const [searchQuery, setSearchQuery] = useState("");
  const [locationQuery, setLocationQuery] = useState(() => savedLocation?.name || "");
  const [coordinates, setCoordinates] = useState<{lat: number;lng: number;} | null>(() =>
    savedLocation ? { lat: savedLocation.lat, lng: savedLocation.lng } : null
  );

  // React to geolocation detection updating savedLocation
  useEffect(() => {
    if (savedLocation && !coordinates) {
      setLocationQuery(savedLocation.name);
      setCoordinates({ lat: savedLocation.lat, lng: savedLocation.lng });
    }
  }, [savedLocation]); // eslint-disable-line react-hooks/exhaustive-deps
  const [visibleCount, setVisibleCount] = useState(8);
  const { data: categories } = useCategories();
  const { data: featuredClasses } = useFeaturedClasses();
  const { user } = useAuth();
  const { data: homeStats } = useHomeStats();
  const navigate = useNavigate();

  // Filter featured classes by distance when coordinates are set
  const { visibleClasses, hasMore, noNearbyClasses } = useMemo(() => {
    if (!featuredClasses) return { visibleClasses: undefined, hasMore: false, noNearbyClasses: false };

    if (coordinates) {
      const classesWithDistance = featuredClasses.
      map((c) => ({
        ...c,
        distance: c.latitude && c.longitude ?
        calculateDistance(coordinates.lat, coordinates.lng, c.latitude, c.longitude) :
        undefined
      })).
      filter((c) => c.distance !== undefined).
      sort((a, b) => (a.distance ?? 0) - (b.distance ?? 0));

      const nearby = classesWithDistance.filter((c) => (c.distance ?? Infinity) <= 30);
      const noNearby = nearby.length === 0;
      const toShow = noNearby ? [] : classesWithDistance.slice(0, visibleCount);

      return {
        visibleClasses: toShow,
        hasMore: !noNearby && classesWithDistance.length > visibleCount,
        noNearbyClasses: noNearby
      };
    }

    return {
      visibleClasses: featuredClasses.slice(0, visibleCount),
      hasMore: featuredClasses.length > visibleCount,
      noNearbyClasses: false
    };
  }, [featuredClasses, coordinates, visibleCount]);
  const [locationError, setLocationError] = useState(false);
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!coordinates) {
      setLocationError(true);
      return;
    }
    setLocationError(false);
    const params = new URLSearchParams();
    if (searchQuery.trim()) {
      params.set("search", searchQuery.trim());
    }
    if (locationQuery.trim()) {
      params.set("location", locationQuery.trim());
    }
    params.set("lat", coordinates.lat.toString());
    params.set("lng", coordinates.lng.toString());
    navigate(`/browse?${params.toString()}`);
  };
  return (
    <div className="flex flex-col">
      {/* Hero Section - Redesigned */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden bg-foreground">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0" role="img" aria-label="Creative learning experiences">
          <picture>
            <source media="(min-width: 1201px)" srcSet="/hero-learning.webp" type="image/webp" />
            <source media="(min-width: 769px)" srcSet="/hero-learning-1024.webp" type="image/webp" />
            <img
              src="/hero-learning-640.webp"
              alt=""
              fetchPriority="high"
              decoding="sync"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </picture>
          <div className="absolute inset-0 bg-gradient-to-r from-foreground/95 via-foreground/80 to-foreground/40" />
        </div>

        <div className="container relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/20 border border-primary/30 mt-8 mb-6 animate-fade-in">
                <Sparkles className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium text-primary">Discover Local Experts</span>
              </div>

              <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-medium tracking-tight mb-6 text-background animate-slide-up">
                Master New Skills
                <span className="block mt-2">
                  <span className="font-bold text-primary">Around Town</span>
                </span>
              </h1>

              <p
                className="text-lg md:text-xl text-background/80 mb-8 max-w-xl animate-slide-up"
                style={{
                  animationDelay: "0.1s"
                }}>
                
                Connect with passionate instructors teaching cooking, art, music, tech, and more. In-person and virtual
                classes designed for your schedule.
              </p>

              {/* Search Bar */}
              <form
                onSubmit={handleSearch}
                className="flex gap-2 max-w-xl animate-slide-up"
                style={{
                  animationDelay: "0.2s"
                }}>
                
                <LocationAutocomplete
                  value={locationQuery}
                  onChange={(v) => {
                    setLocationQuery(v);
                    if (locationError) setLocationError(false);
                  }}
                  onSelect={(location) => {
                    setLocationQuery(location.name);
                    setCoordinates({ lat: location.lat, lng: location.lng });
                    saveLocation(location.name, location.lat, location.lng);
                    setLocationError(false);
                  }}
                  placeholder="City or ZIP code"
                  className="flex-1 [&_input]:h-14 [&_input]:text-lg [&_input]:rounded-xl [&_input]:bg-background/95 [&_input]:border-0 [&_input]:shadow-lg [&_button]:h-14 [&_button]:rounded-xl" />
                

                <Button type="submit" size="lg" className="h-14 px-8 rounded-xl gradient-primary shadow-gold">
                  Search
                </Button>
              </form>
              {locationError &&
              <p className="text-sm text-destructive mt-2 animate-fade-in flex items-center gap-1.5" style={{ animationDelay: "0.2s" }}>
                  <MapPin className="h-3.5 w-3.5" />
                  Please select a location to search for classes
                </p>
              }

              {/* Quick Stats */}
              <div
                className="flex flex-wrap gap-8 mt-10 mb-8 animate-slide-up"
                style={{
                  animationDelay: "0.3s"
                }}>
                
                {[
                {
                  value:
                  homeStats && homeStats.instructorCount > 21 ?
                  `${homeStats.instructorCount.toLocaleString()}+` :
                  "21+",
                  label: "Instructors"
                },
                {
                  value:
                  homeStats && homeStats.studentCount > 0 ? `${homeStats.studentCount.toLocaleString()}+` : "176+",
                  label: "Students"
                },
                {
                  value: homeStats?.avgRating ? homeStats.avgRating.toFixed(1) : "4.7",
                  label: "Avg Rating",
                  icon: <Star className="h-4 w-4 fill-primary text-primary" />
                }].
                map((stat) =>
                <div key={stat.label} className="text-background/90">
                    <div className="flex items-center gap-1 font-serif text-2xl font-medium">
                      {stat.value}
                      {stat.icon}
                    </div>
                    <div className="text-sm text-background/60">{stat.label}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Right Content - Featured Category Cards */}
            <div className="hidden lg:grid grid-cols-2 gap-4">
              {[
              {
                img: cookingImage,
                title: "Cooking",
                classes: "120+ classes"
              },
              {
                img: artImage,
                title: "Art & Design",
                classes: "85+ classes"
              },
              {
                img: musicImage,
                title: "Music",
                classes: "95+ classes"
              }].
              map((category, index) =>
              <div
                key={category.title}
                className={`relative overflow-hidden rounded-2xl ${index === 0 ? "col-span-2 aspect-[2/1]" : "aspect-square"} animate-slide-up`}
                style={{
                  animationDelay: `${0.2 + index * 0.1}s`
                }}>
                
                  <img
                  src={category.img}
                  alt={category.title}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  decoding="async" />
                

                  <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 via-foreground/20 to-transparent" />
                  <div className="absolute bottom-4 left-4 text-background">
                    <h2 className="font-serif text-xl font-medium">{category.title}</h2>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Referral Program Section moved below "Turn Your Skills Into Income" */}

      {/* Featured Classes Section */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-t from-muted/90 via-muted/70 to-muted/90" />
        <div className="container relative z-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
            <div>
              <h2 className="font-serif text-3xl md:text-4xl font-medium mb-2">Upcoming Classes</h2>
              <p className="text-muted-foreground max-w-xl">
                Discover exciting learning opportunities happening near you. From cooking to coding, find your next
                skill adventure.
              </p>
            </div>
            <Button variant="outline" asChild className="shrink-0">
              <Link to="/browse">
                View All Classes <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>

          {noNearbyClasses &&
          <div className="mb-8 rounded-xl border border-dashed border-primary/30 bg-primary/5 p-8 text-center">
              <MapPin className="h-10 w-10 text-primary mx-auto mb-3" />
              <h3 className="font-serif text-xl font-medium mb-2">No classes in your area just yet</h3>
              <p className="text-muted-foreground mb-5 max-w-md mx-auto">
                Be the first to teach a class near {locationQuery || 'your location'}! Share your skills with your community.
              </p>
              <Button asChild>
                <Link to={user ? '/teach' : '/auth?mode=signup'}>
                  <PlusCircle className="mr-2 h-4 w-4" />
                  Start a Class
                </Link>
              </Button>
            </div>
          }

          {visibleClasses && visibleClasses.length > 0 ?
          <>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {visibleClasses.map((classItem) =>
              <ClassCard key={classItem.id} classItem={classItem} />
              )}
              </div>

              {hasMore &&
            <div className="text-center mt-8">
                  <Button variant="outline" size="lg" onClick={() => setVisibleCount((prev) => prev + 8)}>
                    Load More Classes <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
            }
            </> :
          !noNearbyClasses ?
          <div className="text-center py-16 bg-card rounded-2xl border-2 border-dashed">
              <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <GraduationCap className="h-8 w-8 text-primary" />
              </div>
              <h3 className="font-serif text-2xl mb-2">No classes yet</h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Be the first to share your skills with the community and start earning!
              </p>
              <Button asChild className="gradient-primary">
                <Link to="/teach">Create Your First Class</Link>
              </Button>
            </div> :
          null}
        </div>
      </section>

      {/* CTA Strip - Find Your Perfect Class */}
      <section className="py-10 gradient-dark">
        <div className="container">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <h3 className="font-serif text-2xl md:text-3xl font-medium text-background mb-2">
                Find Your Perfect Class Today
              </h3>
              <p className="text-background/70">Classes available in cooking, art, music, tech, and more</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button size="lg" className="gradient-primary" asChild>
                <Link to="/browse">
                  <Search className="mr-2 h-4 w-4" />
                  Search Classes
                </Link>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="bg-background text-foreground border-border hover:bg-background/90"
                asChild>
                <Link to="/browse?type=virtual">Virtual Classes</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-muted/40 via-muted/20 to-muted/40" />
        <div className="container relative z-10">
          <div className="flex items-end justify-between mb-8">
            <div>
              <h2 className="font-serif text-3xl md:text-4xl font-medium mb-2">Explore Categories</h2>
              <p className="text-muted-foreground">Find classes that match your interests</p>
            </div>
            <Button variant="ghost" asChild className="hidden md:flex">
              <Link to="/browse">
                View All <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {categories?.slice(0, 8).map((category) =>
            <CategoryCard key={category.id} category={category} />
            )}
          </div>

          <div className="mt-6 text-center md:hidden">
            <Button variant="outline" asChild>
              <Link to="/browse">View All Categories</Link>
            </Button>
          </div>
        </div>
      </section>


      {/* CTA Strip */}
      <section className="py-10 gradient-primary">
        <div className="container">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <h3 className="font-serif text-2xl md:text-3xl font-medium text-primary-foreground mb-2">
                Ready to Start Your Learning Journey?
              </h3>
              <p className="text-primary-foreground/80">Join learners discovering new skills every day</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button size="lg" variant="secondary" asChild>
                <Link to="/browse">Browse Classes</Link>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="bg-transparent border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10"
                asChild>
                
                <Link to={user ? "/teach" : "/auth?mode=signup"}>Become an Instructor</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Instructor Recruitment Section */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        {/* Subtle Background */}
        <div className="absolute inset-0 bg-gradient-to-r from-muted/40 via-background to-muted/20" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-accent/10 rounded-full blur-3xl" />
        <div className="container relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 gradient-primary">
                <DollarSign className="h-3 w-3 mr-1" />
                Earn Money Teaching
              </Badge>
              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-medium mb-6">
                Turn Your Skills Into <span className="font-bold text-primary">Income</span>
              </h2>
              <p className="text-lg text-muted-foreground mb-8">Join instructors earning money doing what they love. 
Set your own schedule, your own prices, and build a community of learners.

              </p>

              <div className="space-y-4 mb-8">
                {[
                "Keep up to 95% of your earnings",
                "Flexible scheduling—teach when you want",
                "Both in-person and virtual options",
                "Free to start, upgrade as you grow"].
                map((item) =>
                <div key={item} className="flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary shrink-0" />
                    <span>{item}</span>
                  </div>
                )}
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" asChild className="gradient-primary">
                  <Link to={user ? "/teach" : "/auth?mode=signup"}>Start Teaching Today</Link>
                </Button>
                <Button size="lg" variant="outline" asChild>
                  <Link to="/earn-money">Learn More About Teaching</Link>
                </Button>
              </div>
            </div>


          </div>
        </div>
      </section>

      {/* FAQs Strip */}
      <section className="relative py-20 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${faqBgImage})` }}
        />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative container mx-auto px-4 text-center max-w-2xl">
          <HelpCircle className="h-8 w-8 text-primary mx-auto mb-4" />
          <h2 className="text-2xl md:text-3xl font-bold mb-3 text-white">Have Questions?</h2>
          <p className="text-white/80 mb-6">
            Find answers about booking classes, teaching on the platform, payments, and more.
          </p>
          <Button size="lg" asChild>
            <Link to="/faqs">
              Browse FAQs <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </section>

    </div>);

}